/* eslint-disable @typescript-eslint/restrict-template-expressions */
import { Box, Typography, useTheme } from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { colorTheme } from 'theme';
import { strings } from 'i18n/Strings';
import { CustomDataGrid } from 'components';
import { commonConstantTexts } from 'constants/constantText';
import { type GridRowData } from 'components/customDataGrid/CustomDataGrid';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAllDetailedUserSalesUnit } from 'service/UserSalesUnitService';
import { formatToSevenFixedDecimals } from 'utils/GeneralUtil';

interface UserWithUnits {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  employeeContribution?: number;
  sellType?: string;
}

const SalesOrderDetails = (props: {
  fundRow: { users: UserWithUnits[] };
}): JSX.Element => {
  const { fundRow } = props;
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const [salesUnitData, setSalesUnitData] = useState([]);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector(
    (state) => state?.pagination?.salesOrderDetailed,
  );

  const getAllDetailedSalesUnits = async (): Promise<void> => {
    try {
      const data = await getAllDetailedUserSalesUnit(
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        Number(fundRow.fundId),
      );

      setSalesUnitData(data?.order);
      setPageCount(data?.count);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void getAllDetailedSalesUnits();
  }, [pageInfo?.page, pageInfo.pageSize]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'userId',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.userId}
        </Typography>
      ),
    },
    {
      field: 'userName',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.userName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {`${row?.userInfo?.firstName || ''} ${row?.userInfo?.lastName || ''}`}
        </Typography>
      ),
    },
    {
      field: 'sellType',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.contributionsLabel}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.type === strings.displayText.fundChangeLabel
            ? strings.displayText.planChangeLabel
            : row.type === strings.displayText.rebalance
            ? strings.displayText.rebalance
            : row.type === strings.displayText.sale && row.requestId
            ? strings.displayText.withdrawal
            : strings.displayText.sale}
        </Typography>
      ),
    },
    {
      field: 'units',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.units}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {formatToSevenFixedDecimals(Number(row.units ?? 0))}
        </Typography>
      ),
    },
  ];

  return (
    <Box padding={1}>
      <CustomDataGrid
        columns={columns}
        rows={salesUnitData}
        getRowId={(row: GridRowData) =>
          `{${row.userId}}-{${row.type}}-{${row.units}}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        pageCount={pageCount}
        currentPage="salesOrderDetailed"
      />
    </Box>
  );
};

export default SalesOrderDetails;
